import styled from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useRef, useState } from 'react';
import Tags from '../../../../func/Tags';
import { COLORS } from '../../../../func/components/Color';
import { ReportFormatNumber } from '../../../../func/Function';

interface DataProps {
  address: string;
  appraisedprice: string;
  bid: string;
  caseno: string;
  courtname?: string;
  court: string;
  img: string;
  minprice: string;
  saledate: string;
  type: string;
  unitno: string;
  viewgray: string;
  viewgray2: string;
  l_area: string;
  b_area: string;
  iidx: string;
  iidx2: number;
  bidprice: number;
  a_per: number;
}
interface Props {
  data: DataProps[];
  title: string;
  status: string;
}

const AiRecommendation = ({ data, title, status }: Props) => {
  const sliderRef = useRef<Slider | null>(null);
  const baseURL = new URL(window.location.href).origin;

  const settings = {
    dots: false,
    infinite: false, // 🔹 마지막 카드 보이게 하려면 false
    speed: 300,
    slidesToShow: 1.2, // 🔹 현재 카드 + 다음 카드 일부 보이게
    slidesToScroll: 1, // 🔹 한 번에 한 장씩 이동
    arrows: false, // 🔹 기본 화살표 비활성화
    swipeToSlide: true, // 🔹 터치 & 마우스로 부드럽게 넘기기
    draggable: true, // 🔹 마우스 드래그 가능
    touchMove: true, // 🔹 터치 이동 가능
    easing: 'ease-in-out', // 🔹 자연스러운 움직임 추가
  };

  const handlePrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const handleNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const renderDateTitleText = (type: string) => {
    if (status === '낙찰') {
      return '낙찰일';
    } else {
      if (type === 'a') {
        return '매각기일';
      } else {
        return '입찰기간';
      }
    }
  };

  const formatSaleDate = (item: any) => {
    if (item.type === 'p') {
      return item.saledate.replace('10:00', '').replace('17:00', '');
    }
    return item.saledate;
  };

  return (
    <Container>
      <Title>{title}</Title>
      <Inner>
        <DataContainer>
          {data.map((item, index) => (
            <SlideCard
              key={index}
              onClick={() => {
                if (item.iidx) {
                  window.open(
                    `${baseURL}/${item.type === 'a' ? 'auction' : 'pauction'}/detail/${item.iidx2}`,
                    '_blank',
                  );
                }
              }}
            >
              <SlideInner>
                <ItemImgBox>
                  <ImgTag>{item.type === 'a' ? '경매' : '공매'}</ImgTag>
                  <img
                    src={item.img}
                    alt=""
                    style={{ width: '100%', height: '100%' }}
                  />
                  <ItemImgTextBox>
                    <ItemImgText>
                      <div>{item.type === 'a' ? '매각기일' : '입찰기간'}</div>
                      <div>{formatSaleDate(item)}</div>
                    </ItemImgText>
                  </ItemImgTextBox>
                </ItemImgBox>
                <ItemDetailBox>
                  <DetailWrapper>
                    <ItemDetail>
                      <InfoWrapper>
                        <Info>
                          <div>{item.caseno}</div>
                          {item.courtname && (
                            <>
                              <div>|</div>
                              <div>{item.courtname}</div>
                            </>
                          )}
                        </Info>
                      </InfoWrapper>
                    </ItemDetail>
                    <ItemDetail>
                      <ItemAddress>{item.address}</ItemAddress>
                    </ItemDetail>
                    <ItemDetail>
                      <ItemAddress>
                        <InfoWrapper>
                          <Info>
                            {item.b_area && Number(item.b_area) !== 0 && (
                              <>
                                <div>
                                  건물 {Number(item.b_area ?? 0).toFixed(1)}m²
                                </div>
                                <div>|</div>
                              </>
                            )}
                            <div>
                              토지 {Number(item.l_area ?? 0).toFixed(1)}m²
                            </div>
                          </Info>
                        </InfoWrapper>
                      </ItemAddress>
                    </ItemDetail>
                  </DetailWrapper>
                  <DetailWrapper>
                    <PriceBox>
                      <div>
                        감정가 {ReportFormatNumber(item.appraisedprice)}{' '}
                      </div>
                      <SalePriceTextBlue>
                        최저가 {ReportFormatNumber(item.minprice)}
                      </SalePriceTextBlue>
                    </PriceBox>
                    <HashTagWrapper>
                      <Tags data={item}></Tags>
                    </HashTagWrapper>
                  </DetailWrapper>
                </ItemDetailBox>
              </SlideInner>
            </SlideCard>
          ))}
        </DataContainer>
      </Inner>
    </Container>
  );
};

export default AiRecommendation;

const SlideInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HashTagWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  break-word: break-word;

  color: rgba(23, 30, 38, 0.78);
  font-size: 12px;
  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  line-height: 17.4px;
  word-wrap: break-word;

  span {
    color: rgba(250, 64, 50, 0.98);
  }
`;

interface TagProps {
  type: string;
}

const DetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Info = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  white-space: nowrap;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: rgba(23, 30, 38, 0.56);
  font-size: 12px;
  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  line-height: 17.4px;
  letter-spacing: -0.7px;

  word-wrap: break-word;
`;

const Tag = styled.div<TagProps>`
  width: 40px;
  paddingb-lock: 2px;
  padding-inline: 8px;
  border-radius: 50px;
  font-family: Spoqa Han Sans Neo;
  font-size: 14x;
  font-weight: 500;
  color: ${(props) => (props.type === 'a' ? '#D84836' : '#0C64CE')};
  background-color: ${(props) => (props.type === 'a' ? '#FFDBD6' : '#DEEDFF')};
`;

const styles = {
  tag: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 'auto',
    borderRadius: 8,
    paddingBlock: '4px',
    paddingInline: '8px',
    border: '1px solid #171E261A',
    fontFamily: 'Spoqa Han Sans Neo',
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: '17.4px',
    color: '#171E268F',
  },
};

const SlideCard = styled.div`
  width: 282px;
  height: auto;
  border-radius: 16px;
  background-color: #fff;
  overflow: hidden;
  // border: 1px solid rgba(112, 115, 124, 0.16);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  margin-bottom: 24px;
`;

const ImgTag = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  border-top-left-radius: 8px;
  border-bottom-right-radius: 8px;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 6px 10px;
  color: #fff;
  font-size: 14px;
  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  line-height: 20.3px;
  word-wrap: break-word;
`;

const ItemImgBox = styled.div`
  position: relative;
  width: 100%;
  height: 178px;
  display: flex;
  // border-bottom: 1px solid ${COLORS.borderGray};
  position: relative;
  overflow: hidden;
  img {
    border-radius: 8px;
  }
`;

const ItemImgTextBox = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 8px 14px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 8px;
  background-color: rgba(0, 0, 0, 0.5);
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;
const ItemImgText = styled.div`
  color: #fff;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.7px;
  line-height: 17.4px;
  white-space: nowrap;
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const ItemDetailBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemDetail = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
`;

const ItemAddress = styled.span`
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.7px;
  color: #222222;
`;

const PriceBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  color: rgba(23, 30, 38, 0.78);
  font-size: 12px;
  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  line-height: 17.4px;
  word-wrap: break-word;
`;

const SalePriceTextBlue = styled.span`
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.7px;
  color: #008aff;
`;

const DataContainer = styled.div`
  height: 100%;
  min-width: fit-content; // ✅ 내부 요소가 넘칠 경우 자동 확장
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 16px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
`;
const Container = styled.div`
  width: 100%;
  padding: 32px 16px;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.div`
  font-family: Spoqa Han Sans Neo;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 145%; /* 31.9px */
  letter-spacing: -0.44px;
  color: #171e26;
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 64px;

  width: 100%;
  height: 100%;

  color: #222222;
  font-size: 18px;
  font-family: Spoqa Han Sans Neo;
  font-weight: 700;
  line-height: 26.1px;
  word-wrap: break-word;
  overflow-x: auto; // ✅ 추가
`;
